import { useNavigation } from "@react-navigation/native";
import React, { useEffect, useMemo, useState, useRef } from "react";
import CustomText from "@components/CustomText";
import IssueTags from "@components/IssueTags";
import ItemMarkingBtn from "@components/ItemMarkingBtn";
import color from "@constants/color";
import { Image, StyleSheet, View } from "react-native";
import { meta } from "@constants/cplp";
import { itemsQuantityStatusV3, markItemLostDamaged } from "@services/api";
import toasts from "@utils/toast";
import { getItem, setItem } from "@lib/storage";
import CpDbHOC from "@components/HOC/CpDbHOC";
import SingleQuantityTooltip from "../../CustomerOrderItems/SingleIssueTooltip/SingleQuantityTooltip";
import ItemsToReturnStrip from "./ItemsToReturnStrip";
import ReturnLagaye from "./ReturnLagayeButton";
import IssuesImagesUpload from "./IssuesImagesUpload";
import LostDamageComponent from "./LostDamageComponent";
import IssueTag from "./IssueTag";
import LostDamageModal from "./LostDamageModal";

const IMAGE_HEIGHT = 110;
const IMAGE_WIDTH = 115;

const PacketCard = ({
  info,
  fetchData,
  can_return = false,
  imagesMandatoryForSamasya = {},
  source,
  isCpDb,
  isOfdCp,
}) => {
  const navigation = useNavigation();

  const [currentStateForSingleQty, setCurrentStateForSingleQty] = useState(
    info?.current_state || {
      wrong_item_received: 0,
      item_damaged: 0,
      not_found: 0,
      partial_not_found: 0,
      ofd_db_lost_damage: 0,
    },
  );
  // images are stored on backend as text array, so we need to convert it to array of objects with url key, as the component expects a URL or source key in images array
  const [images, setImages] = useState({
    wrong_item_received:
      info?.wrong_item_received_images?.map((i) => ({ url: i })) || [],
    item_damaged: info?.item_damaged_images?.map((i) => ({ url: i })) || [],
    partial_not_found:
      info?.partial_not_found_images?.map((i) => ({ url: i })) || [],
  });
  const [reason, setReason] = useState(
    info?.current_state?.item_damaged
      ? "item_damaged"
      : info?.current_state?.wrong_item_received
        ? "wrong_item_received"
        : info?.current_state?.partial_not_found
          ? "partial_not_found"
          : "",
  );
  const [visible, setVisible] = useState(false);
  const [showLostDamageModal, setShowLostDamageModal] = useState(false);
  const firstUpdate = useRef(true);
  const foundAllCall = useRef(false);
  const [isReverted, setIsReverted] = useState(false);
  const [lostDamageCount, setLostDamageCount] = useState(
    info?.ofd_state?.lost_damage || 0,
  );

  const totalDefectiveItem =
    (info?.current_state?.item_damaged || 0) +
    (info?.current_state?.not_found || 0) +
    (info?.current_state?.wrong_item_received || 0) +
    (info?.current_state?.partial_not_found || 0) +
    (info?.current_state?.ofd_db_lost_damage || 0);

  const isFound = info?.total_quantity === info?.current_state?.found;
  const showImageCon = useMemo(() => {
    return (
      (imagesMandatoryForSamasya?.item_damaged
        ? currentStateForSingleQty?.item_damaged || 0
        : 0) +
        (imagesMandatoryForSamasya?.wrong_item_received
          ? currentStateForSingleQty?.wrong_item_received || 0
          : 0) +
        (imagesMandatoryForSamasya?.partial_not_found
          ? currentStateForSingleQty?.partial_not_found || 0
          : 0) >
        0 &&
      reason &&
      info?.total_quantity === 1
    );
  }, [
    currentStateForSingleQty,
    info?.total_quantity,
    reason,
    imagesMandatoryForSamasya,
  ]);

  const saveMarkingData = async function () {
    try {
      if (
        showImageCon &&
        Object.values(images).reduce((acc, each) => acc + each.length, 0) === 0
      ) {
        toasts.showShortToast("कृप्या फोटो डालें!");
        setVisible(false);
        return;
      }
      const payload = {
        order_item_id: info?.order_item_id,
        sku_id: info?.sku_id || info?.child_sku_id,
        total_quantity: info?.total_quantity,
        item_number: info?.item_number,
        ofd_state: info?.can_mark_v2,
        new_state: {
          found:
            info?.total_quantity -
            currentStateForSingleQty?.wrong_item_received -
            currentStateForSingleQty?.item_damaged -
            currentStateForSingleQty?.not_found -
            currentStateForSingleQty?.partial_not_found -
            currentStateForSingleQty?.ofd_db_lost_damage,
          item_damaged: currentStateForSingleQty.item_damaged,
          wrong_item_received: currentStateForSingleQty?.wrong_item_received,
          not_found: currentStateForSingleQty?.not_found,
          partial_not_found: currentStateForSingleQty?.partial_not_found,
          ofd_db_lost_damage: currentStateForSingleQty?.ofd_db_lost_damage,
        },
        issue_images: {
          wrong_item_received: images?.wrong_item_received?.map(
            (i) => i?.url && i.url,
          ),
          item_damaged: images?.item_damaged?.map((i) => i?.url && i.url),
          partial_not_found: images?.partial_not_found?.map(
            (i) => i?.url && i.url,
          ),
        },
      };

      await itemsQuantityStatusV3(payload);
      fetchData();
      setVisible(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (isReverted) setIsReverted(false);
    else saveMarkingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStateForSingleQty]);

  useEffect(() => {
    firstUpdate.current = true;
    setCurrentStateForSingleQty(info?.current_state);
  }, [info?.current_state]);

  const onImageChange = (issue, issueImages) => {
    if (images[issue]) {
      setImages((p) => ({
        ...p,
        [issue]: issueImages,
      }));
    }
    setCurrentStateForSingleQty((p) => ({ ...p }));
  };

  const revertStateForSingleQty = () => {
    setImages({
      wrong_item_received:
        info?.wrong_item_received_images?.map((i) => ({ url: i })) || [],
      item_damaged: info?.item_damaged_images?.map((i) => ({ url: i })) || [],
      partial_not_found:
        info?.partial_not_found_images?.map((i) => ({ url: i })) || [],
    });
    setIsReverted(true);
    setReason(
      info?.current_state?.item_damaged
        ? "item_damaged"
        : info?.current_state?.wrong_item_received
          ? "wrong_item_received"
          : info?.current_state?.partial_not_found
            ? "partial_not_found"
            : "",
    );
    setCurrentStateForSingleQty(
      info?.current_state || {
        wrong_item_received: 0,
        item_damaged: 0,
        not_found: 0,
        partial_not_found: 0,
        found: 0,
        ofd_db_lost_damage: 0,
      },
    );
  };

  // for single quantity items
  const onCurrentStateChange = ({
    wrong_item_received = 0,
    item_damaged = 0,
    not_found = 0,
    partial_not_found = 0,
    ofd_db_lost_damage = 0,
  }) => {
    if (wrong_item_received) {
      setReason("wrong_item_received");
    }
    if (item_damaged) {
      setReason("item_damaged");
    }
    if (partial_not_found) {
      setReason("partial_not_found");
    }

    setImages({
      wrong_item_received: [],
      item_damaged: [],
      partial_not_found: [],
    });
    setVisible(false);

    setCurrentStateForSingleQty((p) => ({
      ...p,
      wrong_item_received,
      item_damaged,
      not_found,
      partial_not_found,
      ofd_db_lost_damage,
    }));
  };
  const foundAll = async () => {
    if (
      foundAllCall.current ||
      info?.current_state?.found == info?.total_quantity
    ) {
      return;
    }
    foundAllCall.current = true;
    const payload = {
      order_item_id: info?.order_item_id,
      sku_id: info?.sku_id || info?.child_sku_id,
      total_quantity: info?.total_quantity,
      item_number: info?.item_number,
      ofd_state: info?.can_mark_v2,
      new_state: {
        found: info?.total_quantity,
        item_damaged: 0,
        wrong_item_received: 0,
        not_found: 0,
        partial_not_found: 0,
        ofd_db_lost_damage: 0,
      },
      issue_images: {
        wrong_item_received: [],
        item_damaged: [],
        partial_not_found: [],
      },
    };
    await itemsQuantityStatusV3(payload);
    await fetchData();
    setVisible(false);
    foundAllCall.current = false;
  };
  const changeLostDamageCount = async (value) => {
    if (value >= 0 && value <= info?.can_mark_max_ofd_count) {
      const count = (await getItem("lostDamageCount")) || 0;
      if (value > info?.ofd_state?.lost_damage && count < 3 && !isCpDb) {
        setShowLostDamageModal(true);
        if (count < 3) {
          setItem("lostDamageCount", `${Number(count) + 1}`);
        }
      }
      try {
        const payload = {
          order_item_id: info?.order_item_id,
          lost_damaged: value,
          source,
        };
        await markItemLostDamaged(payload);
        setLostDamageCount(value);
        fetchData();
      } catch (error) {}
    }
  };

  return (
    <View style={styles.mainContainer}>
      <View style={{ flexDirection: "row" }}>
        <View style={styles.imageContainer}>
          <View style={styles.quantityCirle}>
            <CustomText variant="caption_2" style={styles.quantityText}>
              {info?.total_quantity}
            </CustomText>
          </View>
          <View style={styles.imageView}>
            <Image style={styles.image} source={{ uri: info?.product_image }} />
          </View>
          {info?.shipment_id ? (
            <View style={styles.packetInfo}>
              <CustomText variant="body_text_3" style={styles.packetText}>
                {meta.shipment_id_label}
              </CustomText>
              <CustomText variant="caption_2" style={styles.packetText}>
                {info?.shipment_id}
              </CustomText>
            </View>
          ) : null}
        </View>
        <View style={styles.textContainer}>
          <CustomText variant="body_text_2" style={styles.nameText}>
            {info?.name}
          </CustomText>
          <View style={styles.quantityContainer}>
            <CustomText variant="body_text_4" style={styles.captionText}>
              {"क्वांटिटी : "}
            </CustomText>
            <CustomText variant="body_text_2" style={styles.nameText}>
              {info?.total_quantity}
            </CustomText>
          </View>
          {(info?.can_mark_v2 === "PRE_OFD" ||
            info?.can_mark_v2 === "POST_OFD") &&
            !can_return && (
              <View style={styles.markContainer}>
                <View style={styles.centerAlign}>
                  {info?.total_quantity === 1 ? (
                    <SingleQuantityTooltip
                      info={info}
                      currentState={currentStateForSingleQty}
                      onSubmit={onCurrentStateChange}
                      visible={visible}
                      setVisible={setVisible}
                      isCpDb={isCpDb}
                      isOfdCp={isOfdCp}
                      source={source}
                    />
                  ) : (
                    <ItemMarkingBtn
                      onPress={() =>
                        navigation.navigate("MultiQuantityIssues", {
                          info,
                          imagesMandatoryForSamasya,
                          isCpDb,
                          isOfdCp,
                          source,
                        })
                      }
                      type="issue"
                      count={totalDefectiveItem}
                    />
                  )}
                </View>
                <View style={styles.centerAlign}>
                  <ItemMarkingBtn
                    onPress={() => foundAll()}
                    isActive={isFound}
                  />
                </View>
              </View>
            )}
          {/* {!can_return &&
            info?.can_mark_v2 === "POST_OFD" &&
            info?.can_mark_max_ofd_count > 0 && (
              <LostDamageComponent
                lostDamageCount={lostDamageCount}
                can_mark_max_ofd_count={info?.can_mark_max_ofd_count}
                changeLostDamageCount={changeLostDamageCount}
              />
            )} */}
          <View style={styles.issueTagsContainer}>
            <IssueTags current_state={info?.current_state} />
          </View>
          {can_return ? (
            <View style={styles.returnContainer}>
              <ReturnLagaye info={info} />
            </View>
          ) : null}
        </View>
      </View>
      {info?.ofd_state?.lost_damage > 0 && info?.can_mark_v2 === "POST_OFD" ? (
        <View
          style={{
            marginTop: 10,
            width: "100%",
            alignItems: "center",
            top: 5,
          }}
        >
          <CustomText variant="caption_4" style={{ color: color.plain_red }}>
            {info?.lost_damage_text}
          </CustomText>
        </View>
      ) : null}
      {info?.return_quantity && !can_return ? (
        <View style={{ marginTop: 20 }}>
          <ItemsToReturnStrip return_quantity={info?.return_quantity} />
        </View>
      ) : null}
      <View style={{ paddingLeft: 6, paddingRight: 6 }}>
        {showImageCon ? (
          <IssuesImagesUpload
            key={`${reason}_ImagesUpload`}
            reason={reason}
            imageRequired={showImageCon}
            // add from API currentImgs
            currentImgs={images[reason] || []}
            setUploadedImage={onImageChange}
            isSingleQty={true}
            info={info}
            revertStateForSingleQty={revertStateForSingleQty}
          />
        ) : null}
      </View>
      <LostDamageModal
        showModal={showLostDamageModal}
        setShowModal={setShowLostDamageModal}
        modalText={info?.lost_damage_text}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    borderColor: "rgba(0, 0, 0, 0.20)",
    borderWidth: 1,
    padding: 8,
    borderRadius: 16,
    marginBottom: 12,
    marginHorizontal: 10,
  },
  imageContainer: {
    width: IMAGE_WIDTH,
  },
  imageView: {
    height: IMAGE_HEIGHT,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: "rgba(0, 0, 0, 0.20)",
    backgroundColor: "white",
    overflow: "hidden",
  },
  image: {
    height: IMAGE_HEIGHT,
    width: IMAGE_WIDTH,
  },
  packetInfo: {
    backgroundColor: color.white,
    borderRadius: 12,
    paddingHorizontal: 8,
    flexDirection: "column",
    alignItems: "center",
    marginTop: 10,
  },
  packetText: {
    color: color.black,
    marginTop: 2,
  },
  textContainer: {
    marginTop: 4,
    marginLeft: 25,
    flex: 1,
  },
  nameText: {
    color: "rgba(0, 0, 0, 0.86)",
  },
  captionText: {
    color: "rgba(0, 0, 0, 0.56)",
  },
  quantityContainer: {
    flexDirection: "row",
    marginTop: 8,
  },
  markContainer: {
    flexDirection: "row",
    marginTop: 20,
  },
  centerAlign: {
    flexDirection: "row",
    width: "50%",
  },
  issueTagsContainer: {
    alignItems: "flex-start",
  },
  returnContainer: {
    alignItems: "flex-start",
  },
  quantityCirle: {
    position: "absolute",
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 15,
    backgroundColor: "black",
    top: -12,
    right: -12,
    zIndex: 1,
  },
  quantityText: {
    color: "white",
  },
});

export default CpDbHOC(PacketCard);
